import type { ChangeEvent, ClipboardEvent } from 'react'

import createFormJson from '../../demo/fixtures/create-form.json'
import type { ChainCreateLinkState } from '../../demo/store'
import type { Product } from '../../demo/types'
import { Button, Card, Icon, Input, Skeleton, Spinner } from '../../ui'
import styles from './CreatePage.module.css'
import { createTakeoverCopy } from './copy'
import type { ScrapeFacts } from './scrape'

export type RecognitionState = ChainCreateLinkState

interface LinkFieldProps {
  value: string
  state: RecognitionState
  product: Product
  facts: ScrapeFacts
  visibleStepCount: number
  steps: { label: string; detail: string }[]
  onChange: (value: string) => void
  onRecognize: (value?: string) => void
}

export function LinkField({ value, state, product, facts, visibleStepCount, steps, onChange, onRecognize }: LinkFieldProps) {
  const copy = createTakeoverCopy.linkCard
  const sellingPoints = facts.chips.split(' / ').filter(Boolean)
  const change = (event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)
  const paste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text')
    if (pasted.trim()) window.setTimeout(() => onRecognize(pasted), 0)
  }
  const retry = () => {
    onChange('')
    window.requestAnimationFrame(() => document.querySelector<HTMLInputElement>('[data-scope="link-intake"] [data-role="link-input"]')?.focus())
  }
  return (
    <Card className={`${styles.takeoverCard} ${state === 'error' ? styles.errorCard : ''}`} data-scope="link-intake">
      <div className={styles.cardHead}>
        <Icon name="link" size={17} />
        <h3>{copy.title}</h3>
        <span className={styles.cardStep}>{copy.step}</span>
      </div>
      <div className={styles.cardBody}>
        <div className={`${styles.linkRow} ${state === 'idle' ? '' : styles.linkRowSettled}`}>
          <Input
            className={styles.linkInput}
            data-role="link-input"
            value={value}
            error={state === 'error'}
            placeholder={copy.placeholder}
            onChange={change}
            onBlur={() => { if (state === 'idle' && value.trim()) onRecognize() }}
            onPaste={paste}
            icon={<button type="button" className={styles.iconButton} aria-label="识别商品链接" onMouseDown={(event) => event.preventDefault()} onClick={() => onRecognize()}><Icon name="link" size={18} /></button>}
          />
          {state === 'idle' ? <Button variant="ghost" className={styles.linkAction} onMouseDown={(event) => event.preventDefault()} onClick={() => onRecognize(product.url)}>{copy.sample}</Button> : null}
          {state === 'loading' ? <span className={`${styles.chip} ${styles.chipAi}`} data-role="link-status">{copy.statusLoading}</span> : null}
          {state === 'recognized' ? <span className={`${styles.chip} ${styles.chipOk}`} data-role="link-status"><Icon name="check" size={13} strokeWidth={2.6} />{copy.statusOk}</span> : null}
          {state === 'error' ? <span className={`${styles.chip} ${styles.chipBad}`} data-role="link-status">{copy.statusBad}</span> : null}
        </div>

        {state === 'idle' ? <p className={`${styles.tiny} ${styles.muted} ${styles.emptyHint}`}>{copy.emptyHint}</p> : null}

        {state === 'loading' ? (
          <>
            <div className={styles.recognitionCard} data-scope="state-recognizing-product">
              <div className={styles.recognitionSteps}>
                {steps.slice(0, visibleStepCount).map((step, index) => (
                  <div className={styles.recognitionStep} key={step.label}>
                    <span>{index === visibleStepCount - 1 ? <Spinner /> : <Icon name="check-circle" size={18} strokeWidth={2} />}</span>
                    <div><b>{step.label}</b><p>{step.detail}</p></div>
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.productCard} data-role="product-card">
              <Skeleton width={66} height={66} radius="var(--r-thumb)" />
              <div className={styles.productSkeletonText}><Skeleton width={180} height={15} /><Skeleton width={120} height={11} /></div>
            </div>
          </>
        ) : null}

        {state === 'recognized' ? (
          <>
            <div className={styles.productCard} data-role="product-card">
              <div data-context="product-thumb"><img src={`/${product.heroImage ?? product.image}`} alt={product.name} /></div>
              <div className={styles.productInfo}>
                <p className={styles.productName} data-role="product-name">{facts.name} {facts.spec}</p>
                <p className={`${styles.tiny} ${styles.muted} ${styles.productSub}`}>售价 {facts.price} · 主图 1 张</p>
                <div className={styles.sellingPoints} data-role="selling-points">{sellingPoints.map((point) => <span className={styles.chip} key={point}>{point}</span>)}</div>
              </div>
            </div>
            <div className={styles.intakeFields} data-role="intake-fields">
              <p><b>{createFormJson.recognizeSummaryLabel}</b>{facts.name} {facts.spec} · 售价 {facts.price}</p>
              <p>标题 {facts.name} {facts.spec} · 价格 {facts.price} · 主图 1 张 · 卖点 {sellingPoints.length} 条（{sellingPoints.join(' / ')}）</p>
            </div>
          </>
        ) : null}

        {state === 'error' ? (
          <>
            <div className={styles.errorBody} data-scope="state-form-error">{copy.errorBody}</div>
            <div className={styles.errorActions}>
              <Button className={styles.linkAction} onClick={retry}>{copy.retry}</Button>
              <Button variant="ghost" className={styles.linkAction} onClick={() => onRecognize(product.url)}>{copy.sample}</Button>
            </div>
          </>
        ) : null}
      </div>
    </Card>
  )
}
